import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/api';
import { useOrgStore } from '@/store/orgStore';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, Download, MoreHorizontal, Pencil, Plus, Search, Trash2, Upload, UserCheck2, XCircle } from 'lucide-react';

const statusColors: Record<string, string> = {
  nuevo: 'bg-[#EFF6FF] text-[#2563EB] border-[#2563EB]/20',
  contactado: 'bg-[#FFFBEB] text-[#B45309] border-[#F59E0B]/20',
  calificado: 'bg-[#DCFCE7] text-[#15803D] border-[#22C55E]/20',
  descartado: 'bg-[#FEE2E2] text-[#B91C1C] border-[#EF4444]/20',
  convertido: 'bg-[#F5F3FF] text-[#7C3AED] border-[#8B5CF6]/20',
};

const sourceLabels: Record<string, string> = {
  web: 'Web',
  referido: 'Referido',
  evento: 'Evento',
  linkedin: 'LinkedIn',
  whatsapp: 'WhatsApp',
  email: 'Email',
};

const openStages = ['Prospecto', 'Contacto realizado', 'Reunion / demo', 'Propuesta enviada', 'Negociacion'];
const emptyLead = { name: '', email: '', phone: '', company: '', source: 'web', status: 'nuevo', notes: '' };
const emptyQualification = { budget: 'No', authority: 'No', need: 'No', urgency: 'No' };

function formatCLP(value: number) {
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(value || 0);
}

export default function LeadsPage() {
  const [leads, setLeads] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editLead, setEditLead] = useState<any>(null);
  const [form, setForm] = useState(emptyLead);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [qualificationLead, setQualificationLead] = useState<any>(null);
  const [qualificationForm, setQualificationForm] = useState(emptyQualification);
  const [discardLead, setDiscardLead] = useState<any>(null);
  const [discardReason, setDiscardReason] = useState('');
  const [convertLead, setConvertLead] = useState<any>(null);
  const [convertForm, setConvertForm] = useState({
    opportunity_name: '',
    monto_neto: 0,
    stage: 'Prospecto',
    probability: 15,
    next_step: '',
    next_step_date: '',
  });
  const { currentOrgId } = useOrgStore();

  const fetchLeads = useCallback(async () => {
    if (!currentOrgId) return;
    const params: any = {};
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    const { data } = await api.get('/leads', { params });
    setLeads(data);
  }, [currentOrgId, search, statusFilter]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const qualificationPreview = useMemo(() => {
    const score = ['budget', 'authority', 'need', 'urgency'].reduce((sum, key) => sum + (qualificationForm[key as keyof typeof qualificationForm] === 'Si' ? 25 : 0), 0);
    return {
      score,
      temperature: score >= 75 ? 'Caliente' : score >= 40 ? 'Medio' : 'Frio',
      qualificationType: score >= 75 ? 'Prospecto' : 'Lead',
    };
  }, [qualificationForm]);

  const openCreate = () => {
    setEditLead(null);
    setForm(emptyLead);
    setDialogOpen(true);
  };

  const openEdit = (lead: any) => {
    setEditLead(lead);
    setForm({
      name: lead.name || '',
      email: lead.email || '',
      phone: lead.phone || '',
      company: lead.company || '',
      source: lead.source || 'web',
      status: ['nuevo', 'contactado', 'calificado'].includes(lead.status) ? lead.status : 'nuevo',
      notes: lead.notes || '',
    });
    setDialogOpen(true);
  };

  const openQualification = (lead: any) => {
    setQualificationLead(lead);
    setQualificationForm({
      budget: lead.qualification?.budget || 'No',
      authority: lead.qualification?.authority || 'No',
      need: lead.qualification?.need || 'No',
      urgency: lead.qualification?.urgency || 'No',
    });
  };

  const openDiscard = (lead: any) => {
    setDiscardLead(lead);
    setDiscardReason(lead.discard_reason || '');
  };

  const openConvert = (lead: any) => {
    setConvertLead(lead);
    setConvertForm({
      opportunity_name: `Oportunidad ${lead.company || lead.name}`,
      monto_neto: 0,
      stage: 'Prospecto',
      probability: 15,
      next_step: 'Agendar reunion / demo',
      next_step_date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editLead) {
        await api.put(`/leads/${editLead.id}`, form);
        toast.success('Lead actualizado');
      } else {
        await api.post('/leads', form);
        toast.success('Lead creado');
      }
      setDialogOpen(false);
      await fetchLeads();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Error al guardar');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/leads/${deleteId}`);
      toast.success('Lead eliminado');
      setDeleteId(null);
      await fetchLeads();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const handleQualify = async () => {
    if (!qualificationLead) return;
    try {
      await api.post(`/leads/${qualificationLead.id}/qualify`, qualificationForm);
      toast.success(`Lead calificado como ${qualificationPreview.qualificationType}`);
      setQualificationLead(null);
      await fetchLeads();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'No fue posible calificar el lead');
    }
  };

  const handleDiscard = async () => {
    if (!discardLead) return;
    try {
      await api.post(`/leads/${discardLead.id}/discard`, { discard_reason: discardReason });
      toast.success('Lead descartado');
      setDiscardLead(null);
      setDiscardReason('');
      await fetchLeads();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'No fue posible descartar el lead');
    }
  };

  const handleConvert = async () => {
    if (!convertLead) return;
    try {
      await api.post(`/leads/${convertLead.id}/convert`, convertForm);
      toast.success('Lead convertido a oportunidad');
      setConvertLead(null);
      await fetchLeads();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'No fue posible convertir el lead');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await api.get('/leads/export/csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'leads.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exportado correctamente');
    } catch {
      toast.error('Error al exportar');
    }
  };

  const handleImportCSV = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post('/leads/import/csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${data.count} leads importados`);
      await fetchLeads();
    } catch {
      toast.error('Error al importar CSV');
    }
    setImporting(false);
    e.target.value = '';
  };

  return (
    <div data-testid="leads-page">
      <div className="flex flex-col gap-3 mb-6 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Leads</h1>
          <p className="text-sm text-[#475569] mt-1">Bandeja operativa para calificar, descartar y convertir prospectos.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={handleExportCSV} className="flex-1 sm:flex-none min-h-11 border-[#E2E8F0] text-[#475569] font-medium" data-testid="export-leads-csv">
            <Download size={16} className="mr-2" /> Exportar CSV
          </Button>
          <div className="relative flex-1 sm:flex-none">
            <input type="file" accept=".csv" onChange={handleImportCSV} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-testid="import-leads-csv-input" />
            <Button variant="outline" className="w-full sm:w-auto min-h-11 border-[#E2E8F0] text-[#475569] font-medium pointer-events-none" disabled={importing}>
              <Upload size={16} className="mr-2" /> {importing ? 'Importando...' : 'Importar CSV'}
            </Button>
          </div>
          <Button onClick={openCreate} className="flex-1 sm:flex-none min-h-11 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold" data-testid="create-lead-btn">
            <Plus size={16} className="mr-2" /> Nuevo Lead
          </Button>
        </div>
      </div>

      <div className="mb-4 grid gap-3 lg:grid-cols-[1fr_180px]">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input placeholder="Buscar leads..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 border-[#E2E8F0]" data-testid="leads-search" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="border-[#E2E8F0]" data-testid="leads-status-filter">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="nuevo">Nuevo</SelectItem>
            <SelectItem value="contactado">Contactado</SelectItem>
            <SelectItem value="calificado">Calificado</SelectItem>
            <SelectItem value="descartado">Descartado</SelectItem>
            <SelectItem value="convertido">Convertido</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="hidden lg:block bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left" data-testid="leads-table">
            <thead>
              <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B]">Lead</th>
                <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] hidden md:table-cell">Fuente</th>
                <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] hidden xl:table-cell">BANT</th>
                <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B]">Estado</th>
                <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] hidden lg:table-cell">Resultado</th>
                <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] w-12"></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.id} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors" data-testid={`lead-row-${lead.id}`}>
                  <td className="py-3 px-4 align-top">
                    <Link to={`/leads/${lead.id}`} className="font-medium text-[#0F172A] hover:text-[#2563EB]">
                      {lead.name}
                    </Link>
                    <p className="text-xs text-[#475569] mt-1">{lead.email || 'Sin email'} · {lead.company || 'Sin empresa'}</p>
                  </td>
                  <td className="py-3 px-4 text-[#475569] hidden md:table-cell">{sourceLabels[lead.source] || lead.source}</td>
                  <td className="py-3 px-4 hidden xl:table-cell">
                    {lead.qualification ? (
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-[#0F172A]">{lead.qualification.score || 0} pts · {lead.qualification.temperature || 'Frio'}</p>
                        <p className="text-[11px] text-[#64748B]">{lead.qualification_type || lead.qualification?.qualification_type || 'Lead'}</p>
                      </div>
                    ) : (
                      <span className="text-xs text-[#94A3B8]">Sin calificar</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <Badge variant="outline" className={`text-xs font-medium capitalize ${statusColors[lead.status] || ''}`}>
                      {lead.status}
                    </Badge>
                  </td>
                  <td className="py-3 px-4 hidden lg:table-cell">
                    {lead.status === 'descartado' ? (
                      <span className="text-xs text-[#B91C1C]">{lead.discard_reason || 'Sin motivo'}</span>
                    ) : lead.status === 'convertido' ? (
                      <span className="text-xs text-[#7C3AED]">Oportunidad creada</span>
                    ) : (
                      <span className="text-xs text-[#64748B]">{lead.notes || 'En gestión'}</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`lead-actions-${lead.id}`}>
                          <MoreHorizontal size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(lead)}>
                          <Pencil size={14} className="mr-2" /> Editar
                        </DropdownMenuItem>
                        {lead.status !== 'convertido' && lead.status !== 'descartado' && (
                          <DropdownMenuItem onClick={() => openQualification(lead)}>
                            <UserCheck2 size={14} className="mr-2" /> Calificar BANT
                          </DropdownMenuItem>
                        )}
                        {lead.status !== 'descartado' && lead.status !== 'convertido' && (
                          <DropdownMenuItem onClick={() => openDiscard(lead)} className="text-[#B91C1C]">
                            <XCircle size={14} className="mr-2" /> Descartar
                          </DropdownMenuItem>
                        )}
                        {lead.status === 'calificado' && (
                          <DropdownMenuItem onClick={() => openConvert(lead)}>
                            <CheckCircle2 size={14} className="mr-2" /> Convertir
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => setDeleteId(lead.id)} className="text-[#EF4444]">
                          <Trash2 size={14} className="mr-2" /> Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
              {leads.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-[#94A3B8]">No hay leads registrados</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ul className="lg:hidden space-y-3" data-testid="leads-card-list">
        {leads.map((lead) => (
          <li key={lead.id} className="rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-sm" data-testid={`lead-card-${lead.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <Link to={`/leads/${lead.id}`} className="font-semibold text-[#0F172A] hover:text-[#2563EB]">
                  {lead.name}
                </Link>
                <p className="text-xs text-[#475569] mt-0.5 truncate">{lead.email || 'Sin email'} · {lead.company || 'Sin empresa'}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className={`text-xs font-medium capitalize ${statusColors[lead.status] || ''}`}>
                  {lead.status}
                </Badge>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-11 w-11" data-testid={`lead-actions-${lead.id}`}>
                      <MoreHorizontal size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openEdit(lead)}>
                      <Pencil size={14} className="mr-2" /> Editar
                    </DropdownMenuItem>
                    {lead.status !== 'convertido' && lead.status !== 'descartado' && (
                      <DropdownMenuItem onClick={() => openQualification(lead)}>
                        <UserCheck2 size={14} className="mr-2" /> Calificar BANT
                      </DropdownMenuItem>
                    )}
                    {lead.status !== 'descartado' && lead.status !== 'convertido' && (
                      <DropdownMenuItem onClick={() => openDiscard(lead)} className="text-[#B91C1C]">
                        <XCircle size={14} className="mr-2" /> Descartar
                      </DropdownMenuItem>
                    )}
                    {lead.status === 'calificado' && (
                      <DropdownMenuItem onClick={() => openConvert(lead)}>
                        <CheckCircle2 size={14} className="mr-2" /> Convertir
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => setDeleteId(lead.id)} className="text-[#EF4444]">
                      <Trash2 size={14} className="mr-2" /> Eliminar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-y-1.5 gap-x-3 text-sm">
              <dt className="text-[#64748B]">Fuente</dt>
              <dd className="text-right text-[#475569]">{sourceLabels[lead.source] || lead.source}</dd>
              <dt className="text-[#64748B]">BANT</dt>
              <dd className="text-right text-[#0F172A]">
                {lead.qualification
                  ? `${lead.qualification.score || 0} pts · ${lead.qualification.temperature || 'Frio'}`
                  : 'Sin calificar'}
              </dd>
              <dt className="text-[#64748B]">Resultado</dt>
              <dd className="text-right text-[#475569]">
                {lead.status === 'descartado'
                  ? (lead.discard_reason || 'Sin motivo')
                  : lead.status === 'convertido'
                    ? 'Oportunidad creada'
                    : (lead.notes || 'En gestión')}
              </dd>
            </dl>
          </li>
        ))}
        {leads.length === 0 && (
          <li className="rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8FAFC] p-8 text-center text-sm text-[#94A3B8]">
            No hay leads registrados
          </li>
        )}
      </ul>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="lead-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {editLead ? 'Editar Lead' : 'Nuevo Lead'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-[#E2E8F0]" data-testid="lead-name-input" />
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Email</Label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border-[#E2E8F0]" data-testid="lead-email-input" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Telefono</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="border-[#E2E8F0]" data-testid="lead-phone-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Empresa</Label>
              <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="border-[#E2E8F0]" data-testid="lead-company-input" />
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Fuente</Label>
                <Select value={form.source} onValueChange={(value) => setForm({ ...form, source: value })}>
                  <SelectTrigger className="border-[#E2E8F0]" data-testid="lead-source-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="web">Web</SelectItem>
                    <SelectItem value="referido">Referido</SelectItem>
                    <SelectItem value="evento">Evento</SelectItem>
                    <SelectItem value="linkedin">LinkedIn</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Estado</Label>
                <Select value={form.status} onValueChange={(value) => setForm({ ...form, status: value })}>
                  <SelectTrigger className="border-[#E2E8F0]" data-testid="lead-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nuevo">Nuevo</SelectItem>
                    <SelectItem value="contactado">Contactado</SelectItem>
                    <SelectItem value="calificado">Calificado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Notas</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border-[#E2E8F0] min-h-[90px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-[#E2E8F0]">Cancelar</Button>
            <Button onClick={handleSave} disabled={saving || !form.name} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="lead-save-btn">
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qualificationLead} onOpenChange={(open) => !open && setQualificationLead(null)}>
        <DialogContent className="sm:max-w-xl" data-testid="lead-qualification-dialog">
          <DialogHeader>
            <DialogTitle>Calificacion BANT</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-3 md:grid-cols-2">
              {[
                ['budget', 'Presupuesto'],
                ['authority', 'Autoridad'],
                ['need', 'Necesidad'],
                ['urgency', 'Urgencia'],
              ].map(([key, label]) => (
                <div key={key}>
                  <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">{label}</Label>
                  <Select value={qualificationForm[key as keyof typeof qualificationForm]} onValueChange={(value) => setQualificationForm((prev) => ({ ...prev, [key]: value }))}>
                    <SelectTrigger className="border-[#E2E8F0]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Si">Si</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
              <p className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Resultado</p>
              <div className="mt-2 grid gap-3 md:grid-cols-3">
                <div>
                  <p className="text-sm text-[#64748B]">Score</p>
                  <p className="text-xl font-semibold text-[#0F172A]">{qualificationPreview.score}</p>
                </div>
                <div>
                  <p className="text-sm text-[#64748B]">Temperatura</p>
                  <p className="text-xl font-semibold text-[#0F172A]">{qualificationPreview.temperature}</p>
                </div>
                <div>
                  <p className="text-sm text-[#64748B]">Calificación</p>
                  <p className="text-xl font-semibold text-[#0F172A]">{qualificationPreview.qualificationType}</p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQualificationLead(null)} className="border-[#E2E8F0]">Cancelar</Button>
            <Button onClick={handleQualify} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
              Guardar calificacion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!discardLead} onOpenChange={(open) => !open && setDiscardLead(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="lead-discard-dialog">
          <DialogHeader>
            <DialogTitle>Descartar lead</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#475569]">El motivo es obligatorio para cerrar el lead como descartado.</p>
            <Textarea value={discardReason} onChange={(e) => setDiscardReason(e.target.value)} className="min-h-[100px] border-[#E2E8F0]" placeholder="Ej: no responde, sin presupuesto, fuera de foco..." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscardLead(null)} className="border-[#E2E8F0]">Cancelar</Button>
            <Button onClick={handleDiscard} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white">Descartar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!convertLead} onOpenChange={(open) => !open && setConvertLead(null)}>
        <DialogContent className="sm:max-w-xl" data-testid="lead-convert-dialog">
          <DialogHeader>
            <DialogTitle>Convertir lead a oportunidad</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-sm text-[#475569]">
              <p><strong className="text-[#0F172A]">{convertLead?.name}</strong> será convertido creando o vinculando empresa, contacto y oportunidad.</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Nombre de oportunidad</Label>
              <Input value={convertForm.opportunity_name} onChange={(e) => setConvertForm((prev) => ({ ...prev, opportunity_name: e.target.value }))} className="border-[#E2E8F0]" />
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Monto neto</Label>
                <Input type="number" value={convertForm.monto_neto || ''} onChange={(e) => setConvertForm((prev) => ({ ...prev, monto_neto: Number(e.target.value) || 0 }))} className="border-[#E2E8F0]" />
                <p className="mt-1 text-xs text-[#64748B]">Bruto estimado: {formatCLP(convertForm.monto_neto + Math.round(convertForm.monto_neto * 0.19))}</p>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Etapa inicial</Label>
                <Select value={convertForm.stage} onValueChange={(value) => setConvertForm((prev) => ({ ...prev, stage: value }))}>
                  <SelectTrigger className="border-[#E2E8F0]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {openStages.map((stage) => (
                      <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Probabilidad</Label>
                <Input type="number" min={0} max={100} value={convertForm.probability} onChange={(e) => setConvertForm((prev) => ({ ...prev, probability: Number(e.target.value) || 0 }))} className="border-[#E2E8F0]" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Fecha próximo paso</Label>
                <Input type="date" value={convertForm.next_step_date} onChange={(e) => setConvertForm((prev) => ({ ...prev, next_step_date: e.target.value }))} className="border-[#E2E8F0]" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Próximo paso</Label>
              <Textarea value={convertForm.next_step} onChange={(e) => setConvertForm((prev) => ({ ...prev, next_step: e.target.value }))} className="min-h-[90px] border-[#E2E8F0]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertLead(null)} className="border-[#E2E8F0]">Cancelar</Button>
            <Button onClick={handleConvert} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">Convertir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent data-testid="lead-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Lead</AlertDialogTitle>
            <AlertDialogDescription>Esta accion no se puede deshacer. El lead sera eliminado permanentemente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[#EF4444] hover:bg-[#DC2626] text-white" data-testid="lead-confirm-delete">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
